import { KeyboardEvent, useId, useMemo, useState } from 'react';

import { Box, Text, Tooltip } from '@chakra-ui/react';
import { ChevronDown, SquareSlash } from 'lucide-react';

import { Avatar } from '@/components/core';
import {
  type PromptUse,
  type SuggestionItemGroup,
} from '@/components/features/Editor';
import { cn } from '@/lib/utils';
import { glassyEffectButton } from '@/styles/variants/glassy';

import { Markdown } from './Markdown';
import { ThirdPartySvg } from './ThirdPartySvg';

export type MessageProps = {
  children: React.ReactNode;
  className?: string;
} & React.HTMLProps<HTMLDivElement>;

const Message = ({ children, className, ...props }: MessageProps) => (
  <div className={cn('flex gap-3', className)} {...props}>
    {children}
  </div>
);

export type MessageAvatarProps = {
  src: string;
  alt: string;
  fallback?: string;
  delayMs?: number;
  className?: string;
};

const MessageAvatar = ({
  src,
  alt,
  fallback,
  delayMs,
  className,
}: MessageAvatarProps) => {
  return (
    <Avatar
      src={src}
      alt={alt}
      fallback={fallback}
      delayMs={delayMs}
      size='sm'
      className={cn('shrink-0', className)}
    />
  );
};

const SlashCommand = ({
  children,
  icon,
}: {
  children: string;
  icon?: string;
}) => {
  return (
    <Box
      data-cy='slash-command-match'
      display='inline-flex'
      bg='rgba(137, 82, 224, 0.2)'
      color='white'
      lineHeight='20px'
      borderRadius={6}
      padding='2px 10px 2px 4px'
    >
      {icon && (
        <ThirdPartySvg src={icon} mt={1} mr={1} height={11} width={11} />
      )}
      <Text as='span'>{children}</Text>
    </Box>
  );
};

/**
 * The record's chips that fit the text, in order: whole ranges inside it that do not
 * overlap. Anything else is dropped, and with nothing left the bubble matches text.
 */
const promptUsesIn = (text: string, prompts?: PromptUse[]): PromptUse[] => {
  const inRange = (prompts ?? [])
    .filter(
      prompt =>
        Array.isArray(prompt?.at) &&
        Number.isInteger(prompt.at[0]) &&
        Number.isInteger(prompt.at[1]) &&
        prompt.at[0] >= 0 &&
        prompt.at[0] < prompt.at[1] &&
        prompt.at[1] <= text.length
    )
    .sort((a, b) => a.at[0] - b.at[0]);

  const uses: PromptUse[] = [];
  for (const prompt of inRange) {
    const last = uses[uses.length - 1];
    if (!last || prompt.at[0] >= last.at[1]) uses.push(prompt);
  }
  return uses;
};

type PromptRecordContentProps = {
  text: string;
  uses: PromptUse[];
  className: string;
};

/**
 * A sent message drawn from its Prompt Templates record: each chip's range shows as
 * a token with the template's title, free text between ranges as written. A token
 * opens the text that was sent for it inside the bubble, under a hairline, so the
 * bubble and its prompt read as one card. A message that is only tokens sits snug
 * around them.
 */
const PromptRecordContent = ({
  text,
  uses,
  className,
}: PromptRecordContentProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const disclosureId = useId();
  const open = openIndex === null ? undefined : uses[openIndex];

  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && openIndex !== null) {
      event.stopPropagation();
      setOpenIndex(null);
    }
  };

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let hasFreeText = false;
  const pushText = (slice: string) => {
    if (slice.trim()) hasFreeText = true;
    parts.push(slice);
  };
  uses.forEach((use, index) => {
    if (use.at[0] > cursor) pushText(text.slice(cursor, use.at[0]));
    const expanded = openIndex === index;
    parts.push(
      <button
        key={`chip-${index}`}
        type='button'
        className='prompt-use-chip'
        data-cy='prompt-use-chip'
        title={use.title}
        aria-expanded={expanded}
        aria-controls={expanded ? disclosureId : undefined}
        onClick={() => setOpenIndex(expanded ? null : index)}
        onKeyDown={closeOnEscape}
      >
        <SquareSlash className='prompt-use-chip__icon' aria-hidden />
        <span className='prompt-use-chip__title'>{use.title}</span>
        <ChevronDown className='prompt-use-chip__chevron' aria-hidden />
      </button>
    );
    cursor = use.at[1];
  });
  if (cursor < text.length) pushText(text.slice(cursor));

  return (
    <Box
      className={cn(
        className,
        'prompt-use',
        !hasFreeText && 'prompt-use--only'
      )}
      lineHeight='24px'
      sx={glassyEffectButton}
    >
      <div className='prompt-use__text'>{parts}</div>
      {open && (
        <div
          id={disclosureId}
          role='region'
          aria-label={`Sent text of ${open.title}`}
          className='prompt-use-disclosure'
          data-cy='prompt-use-disclosure'
          onKeyDown={closeOnEscape}
        >
          <div className='prompt-use-disclosure__label' aria-hidden>
            Sent prompt{open.edited ? ' · edited' : ''}
          </div>
          <div
            className='prompt-use-disclosure__body'
            data-cy='prompt-use-disclosure-body'
          >
            {text.slice(open.at[0], open.at[1])}
          </div>
        </div>
      )}
    </Box>
  );
};

export type MessageContentProps = {
  children: React.ReactNode;
  possibleCommands?: SuggestionItemGroup[];
  markdown?: boolean;
  className?: string;
  /**
   * The Prompt Templates record of a sent message (spec FSI #1226). When it has
   * chips that fit the text, they replace the text-match highlighting.
   */
  prompts?: PromptUse[];
};

const MessageContent = ({
  children,
  markdown = false,
  possibleCommands,
  className,
  prompts,
}: MessageContentProps) => {
  const classNames = cn('p-2 prose break-words whitespace-normal', className);
  const promptUses = useMemo(
    () =>
      !markdown && typeof children === 'string'
        ? promptUsesIn(children, prompts)
        : [],
    [markdown, children, prompts]
  );

  const content = useMemo(() => {
    if (markdown || typeof children !== 'string' || !possibleCommands?.length) {
      return children;
    }

    const possibleCommandsText = possibleCommands.flatMap(group => group.items);

    const escapeRegExp = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const commandRegex = new RegExp(
      `(${possibleCommandsText.map(escapeRegExp).join('|')})`,
      'g'
    );

    const parts = children.split(commandRegex);

    const parsedParts = parts.map((part, index) => {
      for (const group of possibleCommands) {
        for (const item of group.items) {
          if (part === item) {
            return (
              <SlashCommand key={index} children={part} icon={group.icon} />
            );
          }
        }
      }

      return part;
    });

    return <>{parsedParts}</>;
  }, [markdown, children, possibleCommands]);

  if (promptUses.length > 0) {
    return (
      <PromptRecordContent
        text={children as string}
        uses={promptUses}
        className={classNames}
      />
    );
  }

  return markdown ? (
    <Box className={classNames} sx={glassyEffectButton}>
      <Markdown>{children as string}</Markdown>
    </Box>
  ) : (
    <Box className={classNames} lineHeight='24px' sx={glassyEffectButton}>
      {content}
    </Box>
  );
};

export type MessageActionsProps = {
  children: React.ReactNode;
  className?: string;
} & React.HTMLProps<HTMLDivElement>;

const MessageActions = ({
  children,
  className,
  ...props
}: MessageActionsProps) => (
  <div
    className={cn('text-muted-foreground flex items-center gap-2', className)}
    {...props}
  >
    {children}
  </div>
);

export type MessageActionProps = {
  className?: string;
  tooltip: React.ReactNode;
  children: React.ReactNode;
} & React.ComponentProps<typeof Tooltip>;

const MessageAction = ({
  tooltip,
  children,
  placement = 'top',
  ...props
}: MessageActionProps) => {
  return (
    <Tooltip label={tooltip} placement={placement} {...props}>
      {children}
    </Tooltip>
  );
};

export {
  Message,
  MessageAvatar,
  MessageContent,
  MessageActions,
  MessageAction,
};
